import { Modal, Input, Button, Space, message as antMessage } from 'antd'
import { Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { t } from '@/config/translations'
import styles from './CopyActionModal.module.css'

const { TextArea } = Input

export interface CopyActionModalProps {
  open: boolean
  title: string
  content: string
  onClose: () => void
}

export function CopyActionModal({ open, title, content, onClose }: CopyActionModalProps) {
  const [editableContent, setEditableContent] = useState(content)
  const [copied, setCopied] = useState(false)

  // Reset state when modal opens/closes
  const handleModalClose = () => {
    setCopied(false)
    setEditableContent(content)
    onClose()
  }

  // Reset content when modal opens with new content
  if (open && editableContent !== content) {
    setEditableContent(content)
    setCopied(false)
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editableContent)
      setCopied(true)
      void antMessage.success(t('copied_to_clipboard') || 'Copied to clipboard!')

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false)
      }, 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
      void antMessage.error(t('copy_failed') || 'Failed to copy')
    }
  }

  return (
    <Modal
      open={open}
      title={title}
      onCancel={handleModalClose}
      width={700}
      footer={
        <Space>
          <Button onClick={handleModalClose}>{t('cancel') || 'Cancel'}</Button>
          <Button
            type="primary"
            icon={copied ? <Check size={16} /> : <Copy size={16} />}
            onClick={() => {
              void handleCopy()
            }}
          >
            {copied ? (t('copied') || 'Copied!') : (t('copy_to_clipboard') || 'Copy to Clipboard')}
          </Button>
        </Space>
      }
    >
      <div className={styles['modalContent']}>
        <p className={styles['modalDescription']}>
          {t('review_and_edit_before_copying') ||
            'Review and edit the content below before copying to clipboard:'}
        </p>
        <TextArea
          value={editableContent}
          onChange={(e) => {
            setEditableContent(e.target.value)
          }}
          rows={15}
          className={styles['textArea']}
          style={{
            fontFamily: 'monospace',
            fontSize: '13px',
          }}
        />
      </div>
    </Modal>
  )
}
